import { useState, useEffect, useCallback } from 'react';
import { Bell, Search, Check, X } from 'lucide-react';
import { contactsAPI } from '../../api/contacts';

export default function AdminHeader({ title, subtitle }) {
  const [search, setSearch] = useState('');
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const loadNotifications = useCallback(async () => {
    try {
      const response = await contactsAPI.getAll({ status: 'new' });
      const contacts = response.data?.contacts || response.data || [];
      
      const newNotifications = contacts.map(contact => ({
        id: contact._id,
        type: 'contact',
        title: 'Nouveau message',
        message: `De ${contact.name}: ${contact.subject}`,
        time: new Date(contact.createdAt),
        read: false,
        data: contact
      }));
      
      setNotifications(newNotifications);
      setUnreadCount(newNotifications.filter(n => !n.read).length);
      
    } catch (error) {
      console.error('Erreur chargement notifications:', error);
    }
  }, []);

  useEffect(() => {
    loadNotifications();
    
    const interval = setInterval(() => {
      loadNotifications();
    }, 60000);
    
    return () => clearInterval(interval);
  }, [loadNotifications]);

  const markAsRead = async (notificationId) => {
    try {
      await contactsAPI.updateStatus(notificationId, 'read');
      
      setNotifications(prev => 
        prev.map(notif => 
          notif.id === notificationId 
            ? { ...notif, read: true }
            : notif
        )
      );
      
      setUnreadCount(prev => Math.max(0, prev - 1));
      
    } catch (error) {
      console.error('Erreur marquer comme lu:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const unreadNotifications = notifications.filter(n => !n.read);
      
      for (const notif of unreadNotifications) {
        await contactsAPI.updateStatus(notif.id, 'read');
      }
      
      setNotifications(prev => 
        prev.map(notif => ({ ...notif, read: true }))
      );
      
      setUnreadCount(0);
      
    } catch (error) {
      console.error('Erreur tout marquer comme lu:', error);
    }
  };

  const formatTime = (date) => {
    const now = new Date();
    const diffMs = now - date;
    const diffMinutes = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMinutes < 1) return 'À l\'instant';
    if (diffMinutes < 60) return `Il y a ${diffMinutes} min`;
    if (diffHours < 24) return `Il y a ${diffHours} h`;
    if (diffDays < 7) return `Il y a ${diffDays} j`;
    
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit'
    });
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
    console.log('Recherche:', e.target.value);
  };

  return (
    <div className="sticky top-0 z-40 flex items-center justify-between bg-gray-800 backdrop-blur-sm border-b border-gray-700/50 px-6 py-4">
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
          {title}
        </h1>
        {subtitle && (
          <p className="text-gray-400 mt-2">
            {subtitle}
          </p>
        )}
      </div>
      
      <div className="flex items-center gap-4">
        <div className="relative hidden md:block">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search size={20} className="text-gray-400" />
          </div>
          <input 
            type="text" 
            placeholder="Rechercher..." 
            value={search}
            onChange={handleSearch}
            className="pl-10 pr-4 py-2 w-64 bg-gray-700/50 border border-gray-600/50 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-300"
          />
        </div>
        
        <div className="relative">
          <button 
            className={`relative p-2 bg-gray-700/50 hover:bg-gray-600 rounded-lg transition-all duration-200 ${unreadCount > 0 ? 'ring-2 ring-red-500/30' : ''}`}
            onClick={() => setShowNotifications(!showNotifications)}
          >
            <Bell size={20} className="text-gray-300" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-gradient-to-r from-red-500 to-pink-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>
          
          {showNotifications && (
            <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-gray-900 border border-gray-700/50 rounded-xl shadow-2xl z-50 animate-slideDown">
              <div className="flex items-center justify-between p-4 border-b border-gray-700/50">
                <h4 className="font-bold text-white">Notifications</h4>
                <div className="flex items-center gap-2">
                  {unreadCount > 0 && (
                    <button 
                      onClick={markAllAsRead}
                      className="flex items-center gap-1 px-3 py-1.5 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 text-sm rounded-lg transition-colors duration-200"
                    >
                      <Check size={14} />
                      Tout lire
                    </button>
                  )}
                  <button 
                    onClick={() => setShowNotifications(false)}
                    className="p-1.5 hover:bg-gray-800 rounded-lg transition-colors duration-200"
                  >
                    <X size={18} className="text-gray-400" />
                  </button>
                </div>
              </div>
              
              <div className="max-h-96 overflow-y-auto">
                {notifications.length === 0 ? (
                  <div className="flex flex-col items-center justify-center p-8 text-gray-400">
                    <div className="p-3 bg-gray-800/50 rounded-full mb-3">
                      <Bell size={24} />
                    </div>
                    <p>Aucune notification</p>
                    <p className="text-sm mt-1">Vous êtes à jour</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-800">
                    {notifications.map(notification => (
                      <div 
                        key={notification.id} 
                        className={`p-4 hover:bg-gray-800/50 transition-colors duration-200 ${notification.read ? 'opacity-75' : ''}`}
                      >
                        <div className="flex justify-between items-start gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <div className={`w-2 h-2 rounded-full ${notification.read ? 'bg-gray-600' : 'bg-green-500 animate-pulse'}`} />
                              <div className="font-medium text-white truncate">
                                {notification.title}
                              </div>
                            </div>
                            <div className="text-sm text-gray-300 mb-1 line-clamp-2">
                              {notification.message}
                            </div>
                            <div className="text-xs text-gray-400">
                              {formatTime(notification.time)}
                            </div>
                          </div>
                          
                          {!notification.read && (
                            <button 
                              onClick={() => markAsRead(notification.id)}
                              className="flex-shrink-0 p-1.5 hover:bg-green-500/10 text-green-400 rounded-lg transition-colors duration-200"
                              title="Marquer comme lu"
                            >
                              <Check size={14} />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {notifications.length > 0 && (
                <div className="p-3 border-t border-gray-800 bg-gray-900/50 rounded-b-xl">
                  <a 
                    href="/admin/contacts" 
                    className="block text-center px-4 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg transition-colors duration-200"
                  >
                    Voir tous les messages
                  </a>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Overlay pour fermer les notifications en cliquant à côté */}
      {showNotifications && (
        <div 
          className="fixed inset-0 z-30"
          onClick={() => setShowNotifications(false)}
        />
      )}
    </div>
  );
}